import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

// Types
interface DeviceGroup {
  id: string;
  name: string;
  description: string;
  device_count: number;
  status: string;
  created_at: string;
}

// Reserved for future use
// interface DeviceLocation {
//   id: string;
//   device_id: string;
//   location: string;
//   last_updated: string;
// }

interface DeviceHardware {
  id: string;
  device_id: string;
  hardware_type: string;
  details: string;
  last_updated: string;
}

interface DeviceSoftware {
  id: string;
  device_id: string;
  software_name: string;
  version: string;
  last_updated: string;
}

// Reserved for future use
// interface DeviceUser {
//   id: string;
//   device_id: string;
//   username: string;
//   last_login: string;
// }

interface DeviceSecurity {
  id: string;
  device_id: string;
  security_status: string;
  last_scan: string;
}

interface DevicePerformance {
  id: string;
  device_id: string;
  cpu_usage: number;
  memory_usage: number;
  last_updated: string;
}

// Reserved for future use
// interface DeviceAlert {
//   id: string;
//   device_id: string;
//   alert_type: string;
//   message: string;
//   severity: string;
//   created_at: string;
// }


const EnhancedDeviceManagement: React.FC = () => {
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data states
  const [devices, setDevices] = useState<any[]>([]);
  const [deviceGroups, setDeviceGroups] = useState<DeviceGroup[]>([]);
  const [deviceHardware, setDeviceHardware] = useState<DeviceHardware[]>([]);
  const [deviceSoftware, setDeviceSoftware] = useState<DeviceSoftware[]>([]);
  const [deviceSecurity, setDeviceSecurity] = useState<DeviceSecurity[]>([]);
  const [devicePerformance, setDevicePerformance] = useState<DevicePerformance[]>([]);

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('EnhancedDeviceManagement: Starting to load data...');
      
      // Load main devices list first
      const devicesData = await apiService.getDevices();
      const selectedDeviceId = devicesData?.[0]?.device_id;

      // Parallel fetches that do not require a device id
      const groupsPromise = apiService.getDeviceGroups();

      // If no device available yet, set enhanced data to empty
      let hardwareData: any[] = [];
      let softwareData: any[] = [];
      let securityData: any[] = [];
      let performanceData: any[] = [];

      if (selectedDeviceId) {
        [hardwareData, softwareData, securityData, performanceData] = await Promise.all([
          apiService.getDeviceHardware(selectedDeviceId),
          apiService.getDeviceSoftware(selectedDeviceId),
          apiService.getDeviceSecurity(selectedDeviceId),
          apiService.getDevicePerformance(selectedDeviceId)
        ]);
      }

      const groupsData = await groupsPromise;

      console.log('EnhancedDeviceManagement: Devices data received:', devicesData);
      console.log('EnhancedDeviceManagement: Groups data received:', groupsData);

      setDevices(devicesData);
      setDeviceGroups(groupsData);
      setDeviceHardware(hardwareData);
      setDeviceSoftware(softwareData);
      setDeviceSecurity(securityData);
      setDevicePerformance(performanceData);
      
      console.log('EnhancedDeviceManagement: Data loaded successfully');
      
    } catch (err) {
      console.error('EnhancedDeviceManagement: Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load device data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  // Tab Button Component
  const TabButton: React.FC<{ id: string; label: string; icon: string; count?: number; active: boolean }> = ({ id, label, icon, count, active }) => (
    <button
      className={`tab-button ${active ? 'active' : ''}`}
      onClick={() => handleTabChange(id)}
    >
      <span className="tab-icon">{icon}</span>
      <span className="tab-label">{label}</span>
      {count !== undefined && <span className="tab-count">{count}</span>}
    </button>
  );

  // Overview Tab
  const OverviewTab = () => {
    // Calculate stats from devices data
    console.log('OverviewTab: Current devices state:', devices);
    const totalDevices = devices.length;
    const compliantDevices = devices.filter(d => d.compliance_status === 'compliant').length;
    const windowsDevices = devices.filter(d => d.os === 'windows').length;
    const nonCompliantDevices = devices.filter(d => d.compliance_status === 'non_compliant').length;
    
    return (
      <div>
        <div className="card">
          <h2 className="card-title">Device Analytics Overview</h2>
          <p style={{ color: '#6b7280', marginBottom: '24px' }}>
            Comprehensive device management and analytics dashboard
          </p>
          
          {/* Overview Stats */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon blue">
                <svg width="24" height="24" fill="white" viewBox="0 0 24 24">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                  <line x1="8" y1="21" x2="16" y2="21"></line>
                  <line x1="12" y1="17" x2="12" y2="21"></line>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-label">Total Devices</div>
                <div className="stat-value">{totalDevices}</div>
                <div className="stat-sub">Managed devices</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon green">
                <svg width="24" height="24" fill="white" viewBox="0 0 24 24">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <path d="m9 12 2 2 4-4"/>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-label">Compliant Devices</div>
                <div className="stat-value">{compliantDevices}</div>
                <div className="stat-sub">Policy compliant</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon yellow">
                <svg width="24" height="24" fill="white" viewBox="0 0 24 24">
                  <circle cx="18" cy="18" r="3"></circle>
                  <circle cx="6" cy="6" r="3"></circle>
                  <path d="M6 21V9a9 9 0 0 1 9 9"/>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-label">Non-Compliant</div>
                <div className="stat-value">{nonCompliantDevices}</div>
                <div className="stat-sub">Require attention</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon purple">
                <svg width="24" height="24" fill="white" viewBox="0 0 24 24">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-label">Windows Devices</div>
                <div className="stat-value">{windowsDevices}</div>
                <div className="stat-sub">Windows OS</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Devices Tab
  const DevicesTab = () => (
    <div>
      <div className="card">
        <h2 className="card-title">Managed Devices</h2>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>
          Complete list of all managed devices in your environment
        </p>
        
        <div className="devices-table">
          <table>
            <thead>
              <tr>
                <th>Device Name</th>
                <th>OS</th>
                <th>Platform</th>
                <th>Status</th>
                <th>Compliance</th>
                <th>Last Seen</th>
                <th>Tags</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((device) => (
                <tr key={device.device_id}>
                  <td>
                    <div className="device-info">
                      <div className="device-name">{device.name}</div>
                      <div className="device-id">{device.device_id}</div>
                    </div>
                  </td>
                  <td>
                    <span className={`os-badge ${device.os}`}>
                      {device.os.toUpperCase()}
                    </span>
                  </td>
                  <td>{device.platform}</td>
                  <td>
                    <span className={`status-badge ${device.status}`}>
                      {device.status}
                    </span>
                  </td>
                  <td>
                    <span className={`compliance-badge ${device.compliance_status}`}>
                      {device.compliance_status}
                    </span>
                  </td>
                  <td>{new Date(device.last_seen).toLocaleDateString()}</td>
                  <td>
                    <div className="tags">
                      {device.tags && device.tags.length > 0 ? (
                        device.tags.slice(0, 3).map((tag: string, index: number) => (
                          <span key={index} className="tag">{tag}</span>
                        ))
                      ) : (
                        <span className="no-tags">No tags</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Groups Tab
  const GroupsTab = () => (
    <div>
      <div className="card">
        <h2 className="card-title">Device Groups</h2>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>
          Manage device groups and their configurations
        </p>
        
        <div className="groups-grid">
          {deviceGroups.map((group) => (
            <div key={group.id} className="group-card">
              <div className="group-header">
                <h3>{group.name}</h3>
                <span className="group-count">{group.device_count} devices</span>
              </div>
              <p className="group-description">{group.description}</p>
              <div className="group-footer">
                <span className="group-status">{group.status}</span>
                <span className="group-date">{new Date(group.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Hardware Tab
  const HardwareTab = () => (
    <div>
      <div className="card">
        <h2 className="card-title">Hardware Information</h2>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>
          Hardware details and specifications for all devices
        </p>
        
        <div className="hardware-list">
          {deviceHardware.map((hardware) => (
            <div key={hardware.id} className="hardware-item">
              <div className="hardware-type">{hardware.hardware_type}</div>
              <div className="hardware-details">{hardware.details}</div>
              <div className="hardware-date">{new Date(hardware.last_updated).toLocaleDateString()}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Software Tab
  const SoftwareTab = () => (
    <div>
      <div className="card">
        <h2 className="card-title">Software Inventory</h2>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>
          Installed software and applications across devices
        </p>
        
        <div className="software-list">
          {deviceSoftware.map((software) => (
            <div key={software.id} className="software-item">
              <div className="software-name">{software.software_name}</div>
              <div className="software-version">{software.version}</div>
              <div className="software-date">{new Date(software.last_updated).toLocaleDateString()}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Security Tab
  const SecurityTab = () => (
    <div>
      <div className="card">
        <h2 className="card-title">Security Status</h2>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>
          Security status and compliance across all devices
        </p>
        
        <div className="security-list">
          {deviceSecurity.map((security) => (
            <div key={security.id} className="security-item">
              <div className="security-status">{security.security_status}</div>
              <div className="security-date">{new Date(security.last_scan).toLocaleDateString()}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Performance Tab
  const PerformanceTab = () => (
    <div>
      <div className="card">
        <h2 className="card-title">Performance Metrics</h2>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>
          System performance and resource utilization
        </p>
        
        <div className="performance-list">
          {devicePerformance.map((performance) => (
            <div key={performance.id} className="performance-item">
              <div className="performance-metrics">
                <span>CPU: {performance.cpu_usage}%</span>
                <span>Memory: {performance.memory_usage}%</span>
              </div>
              <div className="performance-date">{new Date(performance.last_updated).toLocaleDateString()}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading device data...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="error-container">
        <div className="error-icon">⚠️</div>
        <h3>Error Loading Data</h3>
        <p>{error}</p>
        <button className="action-btn" onClick={loadData}>Retry</button>
      </div>
    );
  }

  return (
    <div>
      {/* Tab Navigation */}
      <div className="tab-navigation">
        <TabButton id="overview" label="Overview" icon="📊" active={activeTab === 'overview'} />
        <TabButton id="devices" label="Devices" icon="💻" count={devices.length} active={activeTab === 'devices'} />
        <TabButton id="groups" label="Groups" icon="📁" count={deviceGroups.length} active={activeTab === 'groups'} />
        <TabButton id="hardware" label="Hardware" icon="🔧" count={deviceHardware.length} active={activeTab === 'hardware'} />
        <TabButton id="software" label="Software" icon="📱" count={deviceSoftware.length} active={activeTab === 'software'} />
        <TabButton id="security" label="Security" icon="🔒" count={deviceSecurity.length} active={activeTab === 'security'} />
        <TabButton id="performance" label="Performance" icon="📈" count={devicePerformance.length} active={activeTab === 'performance'} />
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab />}
      {activeTab === 'devices' && <DevicesTab />}
      {activeTab === 'groups' && <GroupsTab />}
      {activeTab === 'hardware' && <HardwareTab />}
      {activeTab === 'software' && <SoftwareTab />}
      {activeTab === 'security' && <SecurityTab />}
      {activeTab === 'performance' && <PerformanceTab />}
    </div>
  );
};

export default EnhancedDeviceManagement;